/**
 * HeatmapImage — carga el SVG remoto del heatmap del proveedor y lo
 * renderiza inline eliminando la etiqueta textual errónea.
 *
 * Motivo:
 *   Los SVG del proveedor incluyen una etiqueta inferior del tipo
 *     "Mario Callealta #11 — 105 events"
 *   donde `105` corresponde en realidad a `positions_recorded`, no a
 *   eventos deportivos. NetScouters no altera los datos del heatmap
 *   (círculos/manchas ni geometría del campo), únicamente elimina
 *   los `<text>` cuyo contenido contenga "events". La leyenda factual
 *   correcta se renderiza como caption del componente contenedor.
 *
 * Estrategia:
 *   1. Fetch del SVG remoto como texto (CORS: allow-origin: *).
 *   2. Parseo DOM y eliminación de los <text> que contengan "events".
 *   3. Render inline (dangerouslySetInnerHTML) preservando la imagen.
 *   4. Fallback: si el fetch falla, se pinta un <img> con clip-path
 *      inferior para ocultar visualmente esa franja textual.
 */
import React, { useEffect, useState } from 'react';
import { Skeleton } from '@/components/ui/skeleton';

export function HeatmapImage({
  url,
  className = '',
  maxHeightPx = 420,
  fallbackImgClassName = 'w-full h-auto object-contain bg-black/5',
  testId,
}) {
  const [svgMarkup, setSvgMarkup] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!url) return undefined;
    let cancelled = false;
    setSvgMarkup(null);
    setFailed(false);
    (async () => {
      try {
        const resp = await fetch(url, { credentials: 'omit' });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const ct = resp.headers.get('content-type') || '';
        const text = await resp.text();
        if (!ct.includes('svg') && !text.trim().startsWith('<svg')) {
          throw new Error('not_svg');
        }
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'image/svg+xml');
        const svg = doc.documentElement;
        if (!svg || svg.nodeName.toLowerCase() !== 'svg') {
          throw new Error('parse_failed');
        }
        svg.querySelectorAll('text').forEach((node) => {
          const raw = (node.textContent || '').toLowerCase();
          if (raw.includes('events')) node.remove();
        });
        svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        svg.style.width = '100%';
        svg.style.height = 'auto';
        svg.style.maxHeight = `${maxHeightPx}px`;
        svg.style.display = 'block';
        if (!cancelled) {
          setSvgMarkup(new XMLSerializer().serializeToString(svg));
        }
      } catch (_err) {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [url, maxHeightPx]);

  if (!url) return null;

  if (svgMarkup) {
    return (
      <div
        className={className}
        data-testid={testId}
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: svgMarkup }}
      />
    );
  }

  if (failed) {
    return (
      <div className={className}>
        <img
          src={url}
          alt="Mapa de actividad del jugador"
          className={fallbackImgClassName}
          style={{ maxHeight: `${maxHeightPx}px`, clipPath: 'inset(0 0 8% 0)' }}
          draggable={false}
          loading="lazy"
          data-testid={testId ? `${testId}-fallback` : undefined}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <Skeleton className="w-full" style={{ height: `${Math.min(320, maxHeightPx)}px` }} />
    </div>
  );
}

export default HeatmapImage;
